import React from 'react';
import { View, Text, Image } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  DollarSign,
  Users,
  BarChart3,
  Target,
  Crown,
  ChevronDown,
  ChevronUp,
  Edit3,
  Trash2,
  Percent,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import type { InvestmentRecord } from '@/lib/state/profile-store';
import type { ListLayoutType } from '@/components/ListLayout';
import { StatusBadge } from '@/components/ui';

type CompanyStatus = 'active' | 'exited' | 'watchlist' | 'at_risk';
type InvestorRole = 'none' | 'board-member' | 'board-observer' | 'advisor';

const STAGES: { value: string; label: string }[] = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'series-c+', label: 'Series C+' },
];

const ADDITIONAL_ROLES: { value: InvestorRole; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'board-observer', label: 'Board Observer' },
  { value: 'board-member', label: 'Board Member' },
];

const SECURITY_TYPES: { value: string; label: string }[] = [
  { value: 'safe', label: 'SAFE' },
  { value: 'convertible-note', label: 'Convertible Note' },
  { value: 'equity', label: 'Equity' },
  { value: 'other', label: 'Other' },
];

const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<CompanyStatus, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> => ({
  active: { label: 'Active', color: colors.traffic.green, bg: `${colors.traffic.green}15`, icon: CheckCircle2 },
  exited: { label: 'Exited', color: colors.primary[600], bg: colors.primary[50], icon: TrendingUp },
  watchlist: { label: 'Watchlist', color: colors.traffic.amber, bg: `${colors.traffic.amber}15`, icon: Clock },
  at_risk: { label: 'At Risk', color: colors.traffic.red, bg: `${colors.traffic.red}15`, icon: AlertCircle },
});

// Format valuation for display
const formatValuation = (value: number | undefined): string => {
  if (!value) return '';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// Calculate IRR (simplified annual return based on holding period)
function calculateIRR(investmentDate: string, investmentAmount: number, currentValue: number): number {
  if (!investmentAmount || investmentAmount <= 0 || !currentValue) return 0;
  const startDate = new Date(investmentDate);
  const today = new Date();
  const yearsHeld = (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
  if (yearsHeld <= 0) return 0;
  return (Math.pow(currentValue / investmentAmount, 1 / yearsHeld) - 1) * 100;
}

interface PortfolioCardProps {
  company: InvestmentRecord;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  isDemo?: boolean;
  layout?: ListLayoutType;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  isMasked?: boolean;
  /** Company logo, when one has been sourced. Falls back to the role badge. */
  logoUrl?: string | null;
}

// Privacy mask helper — replaces financial values with bullet dots
const mask = (value: string, isMasked: boolean) => isMasked ? '••••••' : value;

export function PortfolioCard({
  company,
  index,
  onEdit,
  onDelete,
  isDemo = false,
  layout = 'list',
  isExpanded = false,
  onToggleExpand,
  isMasked = false,
  logoUrl,
}: PortfolioCardProps) {
  const themeColors = useThemeColors();
  const STATUS_CONFIG = getStatusConfig(themeColors);
  const status = company.status ?? 'active';
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;

  // Calculate financial metrics
  const investmentAmount = company.investmentAmount ?? 0;
  const currentValuation = company.currentValuation ?? company.valuationAtInvestment ?? 0;
  const ownershipPct = company.ownership ?? 0;
  const currentValue = currentValuation * ownershipPct / 100;
  const roi = investmentAmount > 0 ? ((currentValue - investmentAmount) / investmentAmount) * 100 : 0;
  const hasFinancialData = investmentAmount > 0 || currentValuation > 0 || ownershipPct > 0;

  // List layout - collapsible full-width rows
  if (layout === 'list') {
    return (
      <Animated.View entering={FadeInDown.delay(50 + index * 20).duration(200)}>
        <View
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: themeColors.surface.primary,
            borderWidth: 1,
            borderColor: themeColors.border.light,
          }}
        >
          {/* Collapsed Header */}
          <AnimatedPressable
            onPress={onToggleExpand}
            className="p-3 flex-row items-center"
          >
            <View
              className="w-10 h-10 rounded-lg items-center justify-center mr-3"
              style={{
                backgroundColor:
                  company.investorRole === 'board-member'
                    ? themeColors.amber.light
                    : company.investorRole === 'advisor' || company.investorRole === 'board-observer'
                    ? themeColors.primary[50]
                    : themeColors.surface.secondary,
              }}
            >
              {/*
                The logo when we have one; otherwise the role glyph exactly as
                before. The role is NOT dropped when a logo exists — it moves to
                the corner badge below, because board seat vs advisor is a
                signal this card is meant to carry.
              */}
              {logoUrl ? (
                <Image
                  source={{ uri: logoUrl }}
                  resizeMode="contain"
                  style={{ width: '100%', height: '100%', borderRadius: 10 }}
                  accessibilityLabel={`${company.companyName} logo`}
                />
              ) : company.investorRole === 'board-member' ? (
                <Crown size={18} color={themeColors.amber.main} />
              ) : company.investorRole === 'advisor' || company.investorRole === 'board-observer' ? (
                <Users size={18} color={themeColors.primary[600]} />
              ) : (
                <Building2 size={18} color={themeColors.text.tertiary} />
              )}
              {logoUrl && company.investorRole && company.investorRole !== 'none' && (
                <View
                  style={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: themeColors.surface.primary,
                  }}
                >
                  {company.investorRole === 'board-member' ? (
                    <Crown size={10} color={themeColors.amber.main} />
                  ) : (
                    <Users size={10} color={themeColors.primary[600]} />
                  )}
                </View>
              )}
            </View>
            <View className="flex-1 min-w-0">
              {/* Single-line: a company name and an "industry \u2022 stage \u2022 year"
                  subtitle are both free-length, and without a cap they reflowed
                  the row to several ragged lines. */}
              <Text
                style={{ color: themeColors.text.primary }}
                className="font-semibold"
                numberOfLines={1}
              >
                {company.companyName}
              </Text>
              <Text
                style={{ color: themeColors.text.tertiary }}
                className="text-xs"
                numberOfLines={1}
              >
                {company.industry ? `${company.industry} \u2022 ` : ''}{STAGES.find((s) => s.value === company.stage)?.label} {'\u2022'} {company.date.substring(0, 4)}
              </Text>
            </View>
            {/* Quick Info \u2014 `shrink-0` so an amount, a role pill and a status
                badge keep their size and the NAME gives way instead. Without it
                this cluster held ~180pt of intrinsic width and squeezed the
                name block down to roughly 120pt. */}
            <View className="flex-row items-center gap-2 mr-2 shrink-0">
              {investmentAmount > 0 && (
                <Text style={{ color: themeColors.text.secondary }} className="text-sm font-medium">
                  {mask(formatValuation(investmentAmount), isMasked)}
                </Text>
              )}
              {company.investorRole && company.investorRole !== 'none' && (
                <View
                  className="px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor:
                      company.investorRole === 'board-member'
                        ? themeColors.amber.light
                        : themeColors.primary[50],
                  }}
                >
                  <Text
                    style={{
                      color:
                        company.investorRole === 'board-member'
                          ? themeColors.amber.main
                          : themeColors.primary[600],
                    }}
                    className="text-xs font-medium"
                    numberOfLines={1}
                  >
                    {ADDITIONAL_ROLES.find((r) => r.value === company.investorRole)?.label}
                  </Text>
                </View>
              )}
              <StatusBadge
                label={statusConfig.label}
                color={statusConfig.color}
                backgroundColor={statusConfig.bg}
              />
            </View>
            {isExpanded ? (
              <ChevronUp size={18} color={themeColors.text.tertiary} />
            ) : (
              <ChevronDown size={18} color={themeColors.text.tertiary} />
            )}
          </AnimatedPressable>

          {/* Expanded Content */}
          {isExpanded && (
            <View className="px-3 pb-3 border-t" style={{ borderColor: themeColors.border.light }}>
              {/* Financial Metrics */}
              {hasFinancialData && (
                <View className="flex-row gap-2 mt-3 flex-wrap">
                  {investmentAmount > 0 && (
                    <View className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                      <Text style={{ color: themeColors.text.tertiary }} className="text-xs">Invested</Text>
                      <Text style={{ color: themeColors.text.primary }} className="font-semibold text-sm">{mask(formatValuation(investmentAmount), isMasked)}</Text>
                    </View>
                  )}
                  {currentValuation > 0 && (
                    <View className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                      <Text style={{ color: themeColors.text.tertiary }} className="text-xs">Valuation</Text>
                      <Text style={{ color: themeColors.text.primary }} className="font-semibold text-sm">{mask(formatValuation(currentValuation), isMasked)}</Text>
                    </View>
                  )}
                  {ownershipPct > 0 && (
                    <View className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                      <Text style={{ color: themeColors.text.tertiary }} className="text-xs">Ownership</Text>
                      <Text style={{ color: themeColors.text.primary }} className="font-semibold text-sm">{mask(`${ownershipPct}%`, isMasked)}</Text>
                    </View>
                  )}
                  {roi !== 0 && investmentAmount > 0 && (
                    <View className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                      <Text style={{ color: themeColors.text.tertiary }} className="text-xs">ROI</Text>
                      {isMasked ? (
                        <Text style={{ color: themeColors.text.tertiary }} className="font-semibold text-sm">••••</Text>
                      ) : (
                        <View className="flex-row items-center gap-1">
                          {roi >= 0 ? (
                            <TrendingUp size={12} color={themeColors.traffic.green} />
                          ) : (
                            <TrendingDown size={12} color={themeColors.traffic.red} />
                          )}
                          <Text style={{ color: roi >= 0 ? themeColors.traffic.green : themeColors.traffic.red }} className="font-semibold text-sm">
                            {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              )}

              {/* Company Metrics.
                  `flex-wrap`: three metrics of free length (revenue, growth,
                  runway) on one unwrapping row overflowed the card once the
                  values grew. Each label is capped so a long masked value
                  cannot push its icon out of view. */}
              {company.metrics && (
                <View className="flex-row items-center flex-wrap gap-x-4 gap-y-2 mt-3 pt-3 border-t" style={{ borderColor: themeColors.border.light }}>
                  {company.metrics.revenue !== undefined && (
                    <View className="flex-row items-center gap-1">
                      <DollarSign size={14} color={themeColors.text.tertiary} />
                      <Text style={{ color: themeColors.text.secondary }} className="text-xs" numberOfLines={1}>
                        {mask(`$${(company.metrics.revenue / 1000).toFixed(0)}K MRR`, isMasked)}
                      </Text>
                    </View>
                  )}
                  {company.metrics.growth !== undefined && (
                    <View className="flex-row items-center gap-1">
                      <BarChart3 size={14} color={themeColors.text.tertiary} />
                      <Text
                        style={{ color: isMasked ? themeColors.text.tertiary : company.metrics.growth >= 0 ? themeColors.traffic.green : themeColors.traffic.red }}
                        className="text-xs font-medium"
                        numberOfLines={1}
                      >
                        {mask(`${company.metrics.growth >= 0 ? '+' : ''}${company.metrics.growth}% growth`, isMasked)}
                      </Text>
                    </View>
                  )}
                  {company.metrics.runway !== undefined && (
                    <View className="flex-row items-center gap-1">
                      <Target size={14} color={themeColors.text.tertiary} />
                      <Text style={{ color: themeColors.text.secondary }} className="text-xs" numberOfLines={1}>
                        {mask(`${company.metrics.runway}mo runway`, isMasked)}
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* Actions */}
              {!isDemo && (
                <View className="flex-row items-center justify-end gap-2 mt-3 pt-3 border-t" style={{ borderColor: themeColors.border.light }}>
                  <AnimatedPressable
                    onPress={onEdit}
                    className="px-4 py-3 rounded-lg flex-row items-center gap-1.5"
                    style={{ backgroundColor: themeColors.primary[50], minHeight: 44 }}
                  >
                    <Edit3 size={16} color={themeColors.primary[600]} />
                    <Text style={{ color: themeColors.primary[600] }} className="text-sm font-medium">Edit</Text>
                  </AnimatedPressable>
                  <AnimatedPressable
                    onPress={onDelete}
                    className="px-4 py-3 rounded-lg flex-row items-center gap-1.5"
                    style={{ backgroundColor: `${themeColors.traffic.red}15`, minHeight: 44 }}
                  >
                    <Trash2 size={16} color={themeColors.traffic.red} />
                    <Text style={{ color: themeColors.traffic.red }} className="text-sm font-medium">Delete</Text>
                  </AnimatedPressable>
                </View>
              )}
            </View>
          )}
        </View>
      </Animated.View>
    );
  }

  // Compact layout - simplified single-line view
  if (layout === 'compact') {
    return (
      <Animated.View entering={FadeInDown.delay(100 + index * 20).duration(200)}>
        <AnimatedPressable
          onPress={onEdit}
          className="rounded-xl overflow-hidden p-3 flex-row items-center"
          style={{
            backgroundColor: themeColors.surface.primary,
            borderWidth: 1,
            borderColor: themeColors.border.light,
          }}
        >
          <View
            className="w-8 h-8 rounded-lg items-center justify-center mr-3"
            style={{
              backgroundColor:
                company.investorRole === 'board-member'
                  ? themeColors.amber.light
                  : themeColors.surface.secondary,
            }}
          >
            {logoUrl ? (
              <Image
                source={{ uri: logoUrl }}
                resizeMode="contain"
                style={{ width: '100%', height: '100%', borderRadius: 8 }}
                accessibilityLabel={`${company.companyName} logo`}
              />
            ) : company.investorRole === 'board-member' ? (
              <Crown size={14} color={themeColors.amber.main} />
            ) : (
              <Building2 size={14} color={themeColors.text.tertiary} />
            )}
          </View>
          <View className="flex-1">
            <Text style={{ color: themeColors.text.primary }} className="font-medium text-sm">
              {company.companyName}
            </Text>
            <Text style={{ color: themeColors.text.tertiary }} className="text-xs">
              {STAGES.find((s) => s.value === company.stage)?.label} {'\u2022'} {company.date.substring(0, 4)}
            </Text>
          </View>
          <StatusBadge
            label={statusConfig.label}
            color={statusConfig.color}
            backgroundColor={statusConfig.bg}
            size="md"
            className="mr-2"
          />
          {company.investmentAmount && (
            <Text style={{ color: themeColors.text.secondary }} className="text-sm font-medium">
              {mask(formatValuation(company.investmentAmount), isMasked)}
            </Text>
          )}
          {!isDemo && (
            <View className="flex-row items-center gap-0 ml-1">
              <AnimatedPressable
                onPress={onEdit}
                className="p-2.5"
                style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <Edit3 size={16} color={themeColors.primary[600]} />
              </AnimatedPressable>
              <AnimatedPressable
                onPress={onDelete}
                className="p-2.5"
                style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <Trash2 size={16} color={themeColors.traffic.red} />
              </AnimatedPressable>
            </View>
          )}
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // Grid mode - calculate IRR (only needed here)
  const irr = investmentAmount > 0 ? calculateIRR(company.date, investmentAmount, currentValue) : 0;

  // Grid mode - traditional card layout
  return (
    <Animated.View entering={FadeInDown.delay(350 + index * 50).duration(500)}>
      <View
        className="rounded-2xl p-4"
        style={{
          backgroundColor: themeColors.surface.primary,
          borderWidth: 1,
          borderColor: themeColors.border.light,
        }}
      >
        {/* Header */}
        <View className="flex-row items-center mb-3">
          <View
            className="w-12 h-12 rounded-xl items-center justify-center mr-3"
            style={{
              backgroundColor:
                company.investorRole === 'board-member'
                  ? themeColors.amber.light
                  : company.investorRole === 'board-observer' || company.investorRole === 'advisor'
                  ? themeColors.primary[50]
                  : themeColors.surface.secondary,
            }}
          >
            {company.investorRole === 'board-member' ? (
              <Crown size={24} color={themeColors.amber.main} />
            ) : company.investorRole === 'advisor' || company.investorRole === 'board-observer' ? (
              <Users size={24} color={themeColors.primary[600]} />
            ) : (
              <Building2 size={24} color={themeColors.primary[600]} />
            )}
          </View>
          <View className="flex-1">
            <View className="flex-row items-center gap-2 flex-wrap">
              <Text style={{ color: themeColors.text.primary }} className="font-semibold text-base">
                {company.companyName}
              </Text>
              <StatusBadge
                label={statusConfig.label}
                color={statusConfig.color}
                backgroundColor={statusConfig.bg}
                icon={StatusIcon}
              />
              {company.investorRole && company.investorRole !== 'none' && (
                <View
                  className="px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor:
                      company.investorRole === 'board-member'
                        ? themeColors.amber.light
                        : themeColors.primary[50],
                  }}
                >
                  <Text
                    style={{
                      color:
                        company.investorRole === 'board-member'
                          ? themeColors.amber.main
                          : themeColors.primary[600],
                    }}
                    className="text-xs font-medium"
                    numberOfLines={1}
                  >
                    {ADDITIONAL_ROLES.find((r) => r.value === company.investorRole)?.label}
                  </Text>
                </View>
              )}
            </View>
            <Text style={{ color: themeColors.text.secondary }} className="text-sm">
              {company.industry ? `${company.industry} \u00b7 ` : ''}
              {STAGES.find((s) => s.value === company.stage)?.label} {'\u00b7'} {company.date.substring(0, 4)}
              {company.securityType && ` \u00b7 ${SECURITY_TYPES.find((s) => s.value === company.securityType)?.label}`}
            </Text>
          </View>
          {!isDemo && (
            <View className="flex-row items-center gap-0">
              <AnimatedPressable
                onPress={onEdit}
                className="p-2.5"
                style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <Edit3 size={18} color={themeColors.primary[600]} />
              </AnimatedPressable>
              <AnimatedPressable
                onPress={onDelete}
                className="p-2.5"
                style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <Trash2 size={18} color={themeColors.traffic.red} />
              </AnimatedPressable>
            </View>
          )}
        </View>

        {/* Key Metrics Row */}
        {hasFinancialData && (
          <>
            <View className="flex-row gap-2 mb-3 flex-wrap">
              {investmentAmount > 0 && (
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">Invested</Text>
                  <Text style={{ color: themeColors.text.primary }} className="font-semibold">{mask(formatValuation(investmentAmount), isMasked)}</Text>
                </View>
              )}
              {currentValuation > 0 && (
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">Valuation</Text>
                  <Text style={{ color: themeColors.text.primary }} className="font-semibold">{mask(formatValuation(currentValuation), isMasked)}</Text>
                </View>
              )}
              {ownershipPct > 0 && (
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">Ownership</Text>
                  <Text style={{ color: themeColors.text.primary }} className="font-semibold">{mask(`${ownershipPct}%`, isMasked)}</Text>
                </View>
              )}
            </View>

            {/* Financial Metrics Row */}
            {investmentAmount > 0 && currentValue > 0 && (
              <View className="flex-row gap-2 flex-wrap">
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">Current Value</Text>
                  <Text style={{ color: themeColors.text.primary }} className="font-semibold">{mask(formatValuation(currentValue), isMasked)}</Text>
                </View>
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">ROI</Text>
                  {isMasked ? (
                    <Text style={{ color: themeColors.text.tertiary }} className="font-semibold">••••</Text>
                  ) : (
                    <View className="flex-row items-center gap-1">
                      {roi >= 0 ? (
                        <TrendingUp size={12} color={themeColors.traffic.green} />
                      ) : (
                        <TrendingDown size={12} color={themeColors.traffic.red} />
                      )}
                      <Text style={{ color: roi >= 0 ? themeColors.traffic.green : themeColors.traffic.red }} className="font-semibold">
                        {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                      </Text>
                    </View>
                  )}
                </View>
                <View className="flex-1 rounded-xl p-3" style={{ backgroundColor: themeColors.surface.secondary, minWidth: 80 }}>
                  <Text style={{ color: themeColors.text.tertiary }} className="text-xs mb-1">IRR</Text>
                  {isMasked ? (
                    <Text style={{ color: themeColors.text.tertiary }} className="font-semibold">••••</Text>
                  ) : (
                    <View className="flex-row items-center gap-1">
                      <Percent size={12} color={irr >= 0 ? themeColors.traffic.green : themeColors.traffic.red} />
                      <Text style={{ color: irr >= 0 ? themeColors.traffic.green : themeColors.traffic.red }} className="font-semibold">
                        {irr >= 0 ? '+' : ''}{irr.toFixed(1)}%
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            )}
          </>
        )}

        {/* Company Metrics */}
        {company.metrics && (
          <View className="flex-row items-center gap-4 pt-3 mt-3 border-t" style={{ borderColor: themeColors.border.light }}>
            {company.metrics.revenue !== undefined && (
              <View className="flex-row items-center gap-1">
                <DollarSign size={14} color={themeColors.text.tertiary} />
                <Text style={{ color: themeColors.text.secondary }} className="text-xs">
                  {mask(`$${(company.metrics.revenue / 1000).toFixed(0)}K MRR`, isMasked)}
                </Text>
              </View>
            )}
            {company.metrics.growth !== undefined && (
              <View className="flex-row items-center gap-1">
                <BarChart3 size={14} color={themeColors.text.tertiary} />
                <Text
                  style={{ color: isMasked ? themeColors.text.tertiary : company.metrics.growth >= 0 ? themeColors.traffic.green : themeColors.traffic.red }}
                  className="text-xs font-medium"
                >
                  {mask(`${company.metrics.growth >= 0 ? '+' : ''}${company.metrics.growth}% growth`, isMasked)}
                </Text>
              </View>
            )}
            {company.metrics.runway !== undefined && (
              <View className="flex-row items-center gap-1">
                <Target size={14} color={themeColors.text.tertiary} />
                <Text style={{ color: themeColors.text.secondary }} className="text-xs">
                  {mask(`${company.metrics.runway}mo runway`, isMasked)}
                </Text>
              </View>
            )}
            {company.metrics.employees !== undefined && (
              <View className="flex-row items-center gap-1">
                <Users size={14} color={themeColors.text.tertiary} />
                <Text style={{ color: themeColors.text.secondary }} className="text-xs">
                  {company.metrics.employees}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Last Update */}
        {company.lastUpdate && (
          <View className="flex-row items-center gap-1 mt-3 pt-3 border-t" style={{ borderColor: themeColors.border.light }}>
            <Calendar size={12} color={themeColors.text.tertiary} />
            <Text style={{ color: themeColors.text.tertiary }} className="text-xs">
              Last update: {company.lastUpdate}
            </Text>
          </View>
        )}
      </View>
    </Animated.View>
  );
}
